import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { UserMessages } from "common/enums/UserMessages";
import { AuthToken } from "common/helpers/auth/auth-token";
import { API_URL } from "config";
import { createContext, MutableRefObject, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type Function = (...args: any[]) => void;

export interface WebSocket {
    state: HubConnectionState;
    onExecutionStarted: (callback: Function) => Function;
    onLoadSuspects: (callback: Function) => Function;
    onLoadErrors: (callback: Function) => Function;
    onLoadLogs: (callback: Function) => Function;
    onLoadNextRuns: (callback: Function) => Function;
    onLoadLastRuns: (callback: Function) => Function;
    onLoadStepStatus: (callback: Function) => Function;
    onSessionStarted: (callback: Function) => Function;
    onSessionFinished: (callback: Function) => Function;
    onLoadMachines: (callback: Function) => Function;
    onAgentConnected: (callback: Function) => Function;
    onAgentDisconnected: (callback: Function) => Function;
    onLoadScheduleStatus: (callback: Function) => Function;
    onNotify: (callback: Function) => Function;
    openConnection: () => void;
    closeConnection: () => void;
}

const WebSocketContext = createContext<WebSocket | null>(null);

export function WebSocketProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<HubConnectionState>(HubConnectionState.Disconnected);
    const onExecutionStartedListeners = useRef<Set<Function>>(new Set());
    const onLoadSuspectsListeners = useRef<Set<Function>>(new Set());
    const onLoadErrorsListeners = useRef<Set<Function>>(new Set());
    const onLoadLogsListeners = useRef<Set<Function>>(new Set());
    const onLoadNextRunsListeners = useRef<Set<Function>>(new Set());
    const onLoadLastRunsListeners = useRef<Set<Function>>(new Set());
    const onLoadStepStatusListeners = useRef<Set<Function>>(new Set());
    const onSessionStartedListeners = useRef<Set<Function>>(new Set());
    const onSessionFinishedListeners = useRef<Set<Function>>(new Set());
    const onLoadMachinesListeners = useRef<Set<Function>>(new Set());
    const onAgentConnectedListeners = useRef<Set<Function>>(new Set());
    const onAgentDisconnectedListeners = useRef<Set<Function>>(new Set());
    const onLoadScheduleStatusListeners = useRef<Set<Function>>(new Set());
    const onNotifyListeners = useRef<Set<Function>>(new Set());

    const connection = useMemo<HubConnection>(() => {
        const token = AuthToken.getToken();
        const connection = new HubConnectionBuilder()
            .withUrl(`${API_URL}/uinotification?token=${token}`, {
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets
            })
            .withAutomaticReconnect()
            .build();

        return connection;
    }, []);

    useEffect(() => {
        connection.onreconnected(() => setState(connection.state));
        connection.onreconnecting(() => setState(connection.state));
        connection.onclose(() => setState(connection.state));

        connection.start()
            .then(() => setState(connection.state))
            .catch(e => console.error(e));

        connection.on(UserMessages[UserMessages.ExecutionStarted], (m) => onExecutionStartedListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.LoadSuspects], (m) => onLoadSuspectsListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.LoadErrors], (m) => onLoadErrorsListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.LoadLogs], (m) => onLoadLogsListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.LoadNextRuns], (m) => onLoadNextRunsListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.LoadLastRuns], (m) => onLoadLastRunsListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.LoadStepStatus], (m) => onLoadStepStatusListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.SessionStarted], (m) => onSessionStartedListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.SessionFinished], (m) => onSessionFinishedListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.LoadMachines], (m) => onLoadMachinesListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.AgentConnected], (m) => onAgentConnectedListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.AgentDisconnected], (m) => onAgentDisconnectedListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.LoadScheduleStatus], (m) => onLoadScheduleStatusListeners.current.forEach((c) => c(m)));
        connection.on(UserMessages[UserMessages.Notify], (m) => onNotifyListeners.current.forEach((c) => c(m)));

        return () => {
            connection.off(UserMessages[UserMessages.ExecutionStarted]);
            connection.off(UserMessages[UserMessages.LoadSuspects]);
            connection.off(UserMessages[UserMessages.LoadErrors]);
            connection.off(UserMessages[UserMessages.LoadLogs]);
            connection.off(UserMessages[UserMessages.LoadNextRuns]);
            connection.off(UserMessages[UserMessages.LoadLastRuns]);
            connection.off(UserMessages[UserMessages.LoadStepStatus]);
            connection.off(UserMessages[UserMessages.SessionStarted]);
            connection.off(UserMessages[UserMessages.SessionFinished]);
            connection.off(UserMessages[UserMessages.LoadMachines]);
            connection.off(UserMessages[UserMessages.AgentConnected]);
            connection.off(UserMessages[UserMessages.AgentDisconnected]);
            connection.off(UserMessages[UserMessages.LoadScheduleStatus]);
            connection.off(UserMessages[UserMessages.Notify]);
            connection.stop();
        };
    }, [connection]);

    const openConnection = useCallback(() => {
        if ([HubConnectionState.Connected, HubConnectionState.Connecting, HubConnectionState.Reconnecting].includes(connection.state))
            return;

        connection
            .start()
            .then(() => setState(connection.state))
            .catch(e => console.error(e));
    }, [connection]);

    const closeConnection = useCallback(() => {
        if (connection.state === HubConnectionState.Disconnected) return;

        connection
            .stop()
            .then(() => setState(connection.state))
            .catch(e => console.error(e));
    }, [connection]);

    const addCallback = useCallback((ref: MutableRefObject<Set<() => void>>, callback: () => void) => {
        ref.current.add(callback);
        return () => ref.current.delete(callback);
    }, []);

    const value = useMemo<WebSocket>(
        () => ({
            state,
            onExecutionStarted: (callback: () => void) => addCallback(onExecutionStartedListeners, callback),
            onLoadSuspects: (callback: () => void) => addCallback(onLoadSuspectsListeners, callback),
            onLoadErrors: (callback: () => void) => addCallback(onLoadErrorsListeners, callback),
            onLoadLogs: (callback: () => void) => addCallback(onLoadLogsListeners, callback),
            onLoadNextRuns: (callback: () => void) => addCallback(onLoadNextRunsListeners, callback),
            onLoadLastRuns: (callback: () => void) => addCallback(onLoadLastRunsListeners, callback),
            onLoadStepStatus: (callback: () => void) => addCallback(onLoadStepStatusListeners, callback),
            onSessionStarted: (callback: () => void) => addCallback(onSessionStartedListeners, callback),
            onSessionFinished: (callback: () => void) => addCallback(onSessionFinishedListeners, callback),
            onLoadMachines: (callback: () => void) => addCallback(onLoadMachinesListeners, callback),
            onAgentConnected: (callback: () => void) => addCallback(onAgentConnectedListeners, callback),
            onAgentDisconnected: (callback: () => void) => addCallback(onAgentDisconnectedListeners, callback),
            onLoadScheduleStatus: (callback: () => void) => addCallback(onLoadScheduleStatusListeners, callback),
            onNotify: (callback: () => void) => addCallback(onNotifyListeners, callback),
            closeConnection,
            openConnection
        }),
        [addCallback, closeConnection, openConnection, state]
    );

    return <WebSocketContext.Provider value={value}>
        {children}
    </WebSocketContext.Provider>;
}

export function useWebSocket(): WebSocket {
    const result = useContext(WebSocketContext);
    if (!result) throw new Error("Invalid use of WebSocket");
    return result;
};